import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Menu,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import {
  CHART_BUILDER_QUESTION_EXAMPLES,
  EXAMPLE_STATUS_LABELS,
} from './chartBuilderQuestionExamples';
import {
  groupOriginalsByCollection,
  isLibraryReference,
  normalizeDashboardLibrary,
} from '../../utils/chartLibrary';
import { isOriginalUnused, originalUsageCount } from '../../utils/originalUsage';

const CHART_TYPE_LABELS = {
  table: 'Tabla',
  bar: 'Barras',
  line: 'Líneas',
  pie: 'Torta',
  doughnut: 'Dona',
  scatter: 'Dispersión',
  intervals: 'Intervalos',
  kpi: 'KPI',
  heatmapGrid: 'Heatmap',
  imageDensity: 'Densidad',
  geoHeatmap: 'Mapa calor geo',
  map: 'Mapa',
};

/** Palabras clave para buscar por tipo (español / inglés / abreviaturas). */
const TYPE_SEARCH_ALIASES = {
  table: ['tabla', 'table', 'grid', 'datos'],
  bar: ['barra', 'barras', 'bar', 'columnas', 'columna'],
  line: ['linea', 'lineas', 'line', 'tendencia', 'serie'],
  pie: ['torta', 'pastel', 'pie', 'circular'],
  radar: ['radar', 'spider', 'arana', 'telarana', 'poligono'],
  doughnut: ['dona', 'doughnut', 'anillo'],
  scatter: ['dispersion', 'scatter', 'puntos', 'nube'],
  intervals: ['intervalos', 'intervals', 'gantt', 'episodios', 'duracion', 'timeline'],
  kpi: ['kpi', 'indicador', 'metrica', 'numero'],
  heatmapGrid: ['heatmap', 'heat', 'grilla', 'celdas', 'imagen'],
  imageDensity: ['densidad', 'density', 'calor', 'imagen'],
  geoHeatmap: ['mapa', 'geo', 'calor', 'osm', 'leaflet'],
  map: ['mapa', 'map', 'geo', 'geografico', 'ubicacion', 'marcador'],
};

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function chartTypeKey(item) {
  return (
    item?.suggestedChartType ||
    item?.chartConfig?.type ||
    item?.wizardState?.visualization?.chartType ||
    item?.wizardState?.chartType ||
    item?.type ||
    ''
  );
}

function chartTypeLabel(item) {
  const t = chartTypeKey(item);
  return CHART_TYPE_LABELS[t] || t || '';
}

function tokenMatchesChartType(token, typeKey, typeLabel) {
  const typeLabelNorm = normalizeSearchText(typeLabel);
  const typeKeyNorm = normalizeSearchText(typeKey);
  if (typeLabelNorm.includes(token) || typeKeyNorm.includes(token)) return true;

  const aliases = TYPE_SEARCH_ALIASES[typeKey] || [];
  if (aliases.some((alias) => alias.includes(token) || token.includes(alias))) return true;

  return Object.entries(CHART_TYPE_LABELS).some(([key, label]) => {
    const labelNorm = normalizeSearchText(label);
    if (labelNorm.includes(token) || normalizeSearchText(key).includes(token)) return true;
    return (TYPE_SEARCH_ALIASES[key] || []).some((alias) => alias.includes(token) || token.includes(alias));
  });
}

/** Filtra por nombre y/o tipo; varias palabras deben coincidir (ej. "barra ingresos"). */
export function matchesChartSearch(query, { title = '', typeKey = '', typeLabel = '' } = {}) {
  const queryNorm = normalizeSearchText(query);
  if (!queryNorm) return true;

  const tokens = queryNorm.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const titleNorm = normalizeSearchText(title);

  return tokens.every(
    (token) =>
      titleNorm.includes(token) ||
      tokenMatchesChartType(token, typeKey, typeLabel || CHART_TYPE_LABELS[typeKey] || typeKey)
  );
}

function GroupHeader({ title, count, expanded, onToggle, icon: Icon = FolderOutlinedIcon }) {
  return (
    <ListItemButton
      onClick={onToggle}
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        mb: 0.25,
        bgcolor: 'action.hover',
      }}
    >
      <Icon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
      <ListItemText
        primary={
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {title} ({count})
          </Typography>
        }
      />
      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </ListItemButton>
  );
}

function OriginalListItem({
  item,
  selected,
  onSelect,
  onDelete,
  onAddToDashboard,
  onCollectionMenu,
  deleting,
  adding,
  canAddToDashboard = false,
  selectable = false,
  checked = false,
  onToggleCheck,
}) {
  const typeLabel = chartTypeLabel(item);
  const usageCount = originalUsageCount(item);

  return (
    <ListItemButton
      selected={selected || checked}
      onClick={() => onSelect?.(item)}
      alignItems="flex-start"
      sx={{
        borderRadius: 1,
        mb: 0.5,
        py: 1,
        px: 1.25,
        ml: 1,
        border: 1,
        borderStyle: 'solid',
        borderColor: selected ? 'primary.main' : checked ? 'primary.light' : 'transparent',
        '&.Mui-selected': { bgcolor: 'action.selected' },
        display: 'flex',
        gap: 0.5,
      }}
    >
      {selectable && (
        <Checkbox
          size="small"
          checked={checked}
          disabled={deleting}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggleCheck?.(item, e.target.checked);
          }}
          sx={{ p: 0.25, mt: 0.125, flexShrink: 0 }}
          inputProps={{ 'aria-label': `Seleccionar ${item.title || 'gráfico'}` }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={selected ? 600 : 500} noWrap>
          {item.title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
          {typeLabel && <Chip size="small" label={typeLabel} variant="outlined" sx={{ height: 22 }} />}
          {!item?.wizardState && (
            <Chip
              size="small"
              label="SQL"
              variant="outlined"
              color="warning"
              sx={{ height: 22 }}
              title="Armado desde el Editor SQL, no desde el Editor de gráficos"
            />
          )}
          {usageCount > 0 && (
            <Chip
              size="small"
              label={`${usageCount} dashboard${usageCount === 1 ? '' : 's'}`}
              variant="outlined"
              color="info"
              sx={{ height: 22 }}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexShrink: 0,
          alignItems: 'flex-start',
          gap: 0.25,
          pt: 0.125,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {onCollectionMenu && (
          <Tooltip title="Cambiar colección">
            <IconButton
              size="small"
              disabled={adding || deleting}
              onClick={(e) => onCollectionMenu(e, item)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canAddToDashboard && onAddToDashboard && (
          <Tooltip title="Agregar al dashboard actual">
            <span>
              <IconButton size="small" disabled={adding || deleting} onClick={() => onAddToDashboard?.(item)}>
                {adding ? <CircularProgress size={16} /> : <AddCircleOutlineIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={usageCount ? 'No se puede eliminar: está en uso' : 'Eliminar de la colección'}>
            <span>
              <IconButton
                size="small"
                disabled={deleting || adding || usageCount > 0}
                onClick={() => onDelete?.(item)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </ListItemButton>
  );
}

function DashboardInstanceItem({
  entry,
  original,
  dashboardId,
  dashboardName,
  selected,
  isReference,
  onViewChartItem,
  onMakeIndependent,
  onRestoreToGrid,
  makingIndependent,
  isCurrentDashboard,
}) {
  const hidden = entry.visibleOnGrid === false;
  const typeLabel = original ? chartTypeLabel(original) : '';
  const showActions = isCurrentDashboard && (isReference || hidden);

  const handleClick = () => {
    if (!original) return;
    onViewChartItem?.(original, {
      entry,
      dashboardId,
      dashboardName,
      isReference: isReference || isLibraryReference(entry),
    });
  };

  return (
    <ListItemButton
      selected={selected}
      onClick={handleClick}
      alignItems="flex-start"
      sx={{
        borderRadius: 1,
        mb: 0.5,
        py: 1,
        px: 1.25,
        ml: 1,
        border: 1,
        borderStyle: isReference ? 'dashed' : 'solid',
        borderColor: selected ? 'primary.main' : isReference ? 'text.secondary' : 'transparent',
        opacity: hidden ? 0.72 : 1,
        '&.Mui-selected': { bgcolor: 'action.selected' },
        display: 'flex',
        gap: 0.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={selected ? 600 : 500} noWrap sx={{ maxWidth: '100%' }}>
            {entry.title || original?.title || 'Sin título'}
          </Typography>
          {isReference && (
            <Chip
              size="small"
              icon={<LinkOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Referencia"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
          {hidden && isCurrentDashboard && (
            <Chip
              size="small"
              icon={<VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />}
              label="No visible"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
        </Box>
        {typeLabel ? (
          <Box sx={{ mt: 0.75 }}>
            <Chip size="small" label={typeLabel} variant="outlined" sx={{ height: 22 }} />
          </Box>
        ) : null}
      </Box>
      {showActions && (
        <Box
          sx={{ display: 'flex', flexShrink: 0, alignItems: 'flex-start', gap: 0.25, pt: 0.125 }}
          onClick={(e) => e.stopPropagation()}
        >
          {isReference && onMakeIndependent && (
            <Tooltip title="Desacoplar: convertir en original independiente">
              <span>
                <IconButton
                  size="small"
                  disabled={makingIndependent}
                  onClick={() => onMakeIndependent?.({ dashboardId, instanceId: entry.id })}
                >
                  {makingIndependent ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CallSplitOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {hidden && onRestoreToGrid && (
            <Tooltip title="Mostrar en el grid">
              <IconButton size="small" onClick={() => onRestoreToGrid(entry.id)}>
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </ListItemButton>
  );
}

function ExampleListItem({ ex, selected, onSelect }) {
  const statusMeta = EXAMPLE_STATUS_LABELS[ex.status] || EXAMPLE_STATUS_LABELS.planned;
  const typeLabel = CHART_TYPE_LABELS[ex.suggestedChartType] || ex.suggestedChartType || '';

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect?.(ex)}
      alignItems="flex-start"
      sx={{
        borderRadius: 1,
        mb: 0.5,
        py: 1,
        px: 1.25,
        border: 1,
        borderColor: selected ? 'primary.main' : 'transparent',
        '&.Mui-selected': { bgcolor: 'action.selected' },
      }}
    >
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={selected ? 600 : 500}>
            {ex.title}
          </Typography>
        }
        secondary={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {typeLabel && <Chip size="small" label={typeLabel} variant="outlined" sx={{ height: 22 }} />}
            <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant="outlined" sx={{ height: 22 }} />
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
      />
    </ListItemButton>
  );
}

/**
 * Colección lateral con dos jerarquías (toggle):
 * - Por colección: solo originales agrupados (marketing, stock, …)
 * - Por dashboard: instancias por dashboard; borde punteado = referencia
 */
export default function ChartBuilderCollectionSidebar({
  getAuthHeaders,
  refreshKey = 0,
  activeExampleId = null,
  activeReferenceInstanceId = null,
  onSelect,
  onViewChartItem,
  disabled = false,
  chartLibrary = [],
  onDeleteError,
  onRestoreToGrid,
  onAddOriginalToDashboard,
  onMakeInstanceIndependent,
  currentDashboardId = null,
  currentDashboardName = '',
  hierarchyMode: controlledHierarchyMode,
  onHierarchyModeChange,
}) {
  const [hierarchyModeInternal, setHierarchyModeInternal] = useState('collection');
  const hierarchyMode = controlledHierarchyMode || hierarchyModeInternal;
  const setHierarchyMode = onHierarchyModeChange || setHierarchyModeInternal;

  const [showExamples, setShowExamples] = useState(false);
  const [originals, setOriginals] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [forkingId, setForkingId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedDashboards, setExpandedDashboards] = useState({});
  const [collectionMenuAnchor, setCollectionMenuAnchor] = useState(null);
  const [collectionMenuItem, setCollectionMenuItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [deleteQueue, setDeleteQueue] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');

  const searchNorm = useMemo(() => normalizeSearchText(searchQuery), [searchQuery]);
  const isSearching = searchNorm.length > 0;

  const loadOriginalsMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/chart-originals', { headers }),
  });
  const loadDashboardsMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/dashboards', { headers }),
  });

  const loadData = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers) {
      setOriginals([]);
      setDashboards([]);
      return;
    }
    setLoading(true);
    try {
      const [origRes, dashRes] = await Promise.all([
        loadOriginalsMutation.mutateAsync(headers),
        loadDashboardsMutation.mutateAsync(headers),
      ]);
      if (origRes.data.success) setOriginals(origRes.data.originals || []);
      if (dashRes.data.success) setDashboards(dashRes.data.dashboards || []);
    } catch {
      setOriginals([]);
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const originalsById = useMemo(() => {
    const map = new Map();
    originals.forEach((o) => map.set(o.id, o));
    return map;
  }, [originals]);

  const collectionGroups = useMemo(() => groupOriginalsByCollection(originals), [originals]);

  const collectionGroupNames = useMemo(
    () => collectionGroups.map(([name]) => name),
    [collectionGroups]
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      collectionGroups.forEach(([name]) => {
        if (next[name] === undefined) next[name] = true;
      });
      return next;
    });
  }, [collectionGroups]);

  useEffect(() => {
    setExpandedDashboards((prev) => {
      const next = { ...prev };
      dashboards.forEach((d) => {
        const key = String(d.id);
        if (next[key] === undefined) next[key] = String(d.id) === String(currentDashboardId);
      });
      return next;
    });
  }, [dashboards, currentDashboardId]);

  const handleDelete = async (item) => {
    const headers = getAuthHeaders?.();
    if (!headers || !String(item?.id || '').startsWith('orig-')) return;
    if (originalUsageCount(item) > 0) {
      onDeleteError?.('Este gráfico está en uso en dashboards. Quítalo de ellos antes de eliminarlo.');
      return;
    }
    setDeleteQueue([item]);
  };

  const handleAddToDashboard = async (item) => {
    if (!onAddOriginalToDashboard || !item?.id) return;
    setAddingId(item.id);
    try {
      const result = await onAddOriginalToDashboard(item);
      if (result) await loadData();
    } finally {
      setAddingId(null);
    }
  };

  const saveOriginalMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-originals', body, { headers }),
  });

  const handleCollectionGroupPick = async (groupName) => {
    setCollectionMenuAnchor(null);
    const item = collectionMenuItem;
    setCollectionMenuItem(null);
    if (!item?.id || !groupName) return;
    const headers = getAuthHeaders?.();
    if (!headers) return;
    try {
      await saveOriginalMutation.mutateAsync({ body: { ...item, collectionGroup: groupName }, headers });
      await loadData();
    } catch (err) {
      onDeleteError?.(err.response?.data?.error || err.message || 'No se pudo cambiar la colección');
    }
  };

  const handleMakeIndependent = async ({ dashboardId, instanceId }) => {
    if (!onMakeInstanceIndependent) return;
    setForkingId(instanceId);
    try {
      await onMakeInstanceIndependent({ dashboardId, instanceId });
      await loadData();
    } finally {
      setForkingId(null);
    }
  };

  const handleSelectOriginal = (item) => {
    if (disabled) return;
    onSelect?.(item);
  };

  const dashboardTree = useMemo(() => {
    return dashboards.map((dash) => {
      const library = normalizeDashboardLibrary(dash);
      const isCurrent = String(dash.id) === String(currentDashboardId);
      const items = library.map((entry) => {
        let mergedEntry = entry;
        if (isCurrent && chartLibrary?.length) {
          const live = chartLibrary.find((e) => e.id === entry.id);
          if (live) mergedEntry = { ...entry, ...live };
        }
        return {
          entry: mergedEntry,
          original: mergedEntry.originalChartId ? originalsById.get(mergedEntry.originalChartId) : null,
          isReference: isLibraryReference(mergedEntry),
        };
      });
      return { dash, items };
    });
  }, [dashboards, originalsById, currentDashboardId, chartLibrary]);

  const unusedOriginalCount = useMemo(
    () => originals.filter((item) => isOriginalUnused(item)).length,
    [originals]
  );

  const filteredCollectionGroups = useMemo(() => {
    return collectionGroups
      .map(([groupName, items]) => [
        groupName,
        items.filter((item) => {
          if (unusedOnly && !isOriginalUnused(item)) return false;
          if (!isSearching) return true;
          return matchesChartSearch(searchQuery, {
            title: item.title,
            typeKey: chartTypeKey(item),
            typeLabel: chartTypeLabel(item),
          });
        }),
      ])
      .filter(([, items]) => items.length > 0);
  }, [collectionGroups, isSearching, searchQuery, unusedOnly]);

  const visibleSelectableItems = useMemo(
    () => (unusedOnly ? filteredCollectionGroups.flatMap(([, items]) => items) : []),
    [unusedOnly, filteredCollectionGroups]
  );

  const checkedVisibleCount = useMemo(
    () => visibleSelectableItems.filter((item) => checkedIds.has(item.id)).length,
    [visibleSelectableItems, checkedIds]
  );

  const allVisibleChecked =
    visibleSelectableItems.length > 0 && checkedVisibleCount === visibleSelectableItems.length;
  const someVisibleChecked = checkedVisibleCount > 0 && !allVisibleChecked;

  useEffect(() => {
    if (!unusedOnly) setCheckedIds(new Set());
  }, [unusedOnly]);

  useEffect(() => {
    if (unusedOriginalCount === 0 && unusedOnly) setUnusedOnly(false);
  }, [unusedOriginalCount, unusedOnly]);

  const toggleChecked = (item, checked) => {
    if (!item?.id) return;
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(item.id);
      else next.delete(item.id);
      return next;
    });
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleChecked) {
      setCheckedIds(new Set());
      return;
    }
    setCheckedIds(new Set(visibleSelectableItems.map((item) => item.id)));
  };

  const openBulkDeleteDialog = () => {
    const items = visibleSelectableItems.filter((item) => checkedIds.has(item.id));
    if (!items.length) return;
    setDeleteQueue(items);
  };

  const closeDeleteDialog = () => {
    if (bulkDeleting) return;
    setDeleteQueue(null);
  };

  const confirmDeleteQueue = async () => {
    const headers = getAuthHeaders?.();
    const items = deleteQueue || [];
    if (!items.length) return;
    if (!headers) {
      onDeleteError?.('No hay sesión para eliminar. Recarga e inténtalo de nuevo.');
      return;
    }
    setBulkDeleting(true);
    try {
      const res = await axios.post(
        '/api/chart-originals/bulk-delete',
        { ids: items.map((item) => item.id) },
        { headers }
      );
      const skipped = res.data?.skippedInUse?.length || 0;
      const deleted = res.data?.deleted?.length || 0;
      const notFound = res.data?.notFound?.length || 0;
      await loadData();
      setCheckedIds(new Set());
      setDeleteQueue(null);
      const reasons = [];
      if (skipped) reasons.push(`${skipped} siguen en uso en algún dashboard`);
      if (notFound) reasons.push(`${notFound} ya no existían (alguien más los borró)`);
      if (!deleted && reasons.length) {
        onDeleteError?.(`Ninguno se eliminó: ${reasons.join('; ')}.`);
      } else if (reasons.length) {
        onDeleteError?.(`Se eliminaron ${deleted}. ${reasons.join('; ')}.`);
      }
    } catch (err) {
      onDeleteError?.(err.response?.data?.error || err.message || 'No se pudo eliminar');
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredDashboardTree = useMemo(() => {
    if (!isSearching) return dashboardTree;
    return dashboardTree
      .map(({ dash, items }) => ({
        dash,
        items: items.filter(({ entry, original }) =>
          matchesChartSearch(searchQuery, {
            title: entry.title || original?.title,
            typeKey: chartTypeKey(original || entry),
            typeLabel: chartTypeLabel(original || entry),
          })
        ),
      }))
      .filter(({ items }) => items.length > 0);
  }, [dashboardTree, isSearching, searchQuery]);

  useEffect(() => {
    if (!isSearching && !unusedOnly) return;
    if (hierarchyMode === 'collection') {
      setExpandedGroups((prev) => {
        const next = { ...prev };
        filteredCollectionGroups.forEach(([name]) => {
          next[name] = true;
        });
        return next;
      });
    } else if (isSearching) {
      setExpandedDashboards((prev) => {
        const next = { ...prev };
        filteredDashboardTree.forEach(({ dash }) => {
          next[String(dash.id)] = true;
        });
        return next;
      });
    }
  }, [isSearching, unusedOnly, hierarchyMode, filteredCollectionGroups, filteredDashboardTree]);

  const builtins = CHART_BUILDER_QUESTION_EXAMPLES;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 280 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { md: 1 },
        borderBottom: { xs: 1, md: 0 },
        borderColor: 'divider',
        minHeight: { md: 420 },
        maxHeight: { xs: 280, md: 'none' },
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CollectionsBookmarkOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Colección</Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1 }}>
        <ToggleButtonGroup
          value={hierarchyMode}
          exclusive
          fullWidth
          size="small"
          onChange={(_, v) => v && setHierarchyMode(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.5, fontSize: '0.72rem', textTransform: 'none' } }}
        >
          <ToggleButton value="collection">
            <FolderOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Colecciones
          </ToggleButton>
          <ToggleButton value="dashboard">
            <DashboardOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Dashboards
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar nombre o tipo (tabla, barra…)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Limpiar búsqueda" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
        {hierarchyMode === 'collection' && unusedOriginalCount > 0 && (
          <FormControlLabel
            sx={{ mt: 0.5, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
            control={
              <Checkbox
                size="small"
                checked={unusedOnly}
                onChange={(e) => setUnusedOnly(e.target.checked)}
              />
            }
            label={`Solo sin uso (${unusedOriginalCount})`}
          />
        )}
        {hierarchyMode === 'collection' && unusedOnly && visibleSelectableItems.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
            <FormControlLabel
              sx={{ ml: 0, mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              control={
                <Checkbox
                  size="small"
                  checked={allVisibleChecked}
                  indeterminate={someVisibleChecked}
                  onChange={handleToggleSelectAllVisible}
                />
              }
              label={allVisibleChecked ? 'Quitar selección' : 'Seleccionar todos'}
            />
            <Button
              size="small"
              color="error"
              disabled={checkedVisibleCount === 0 || bulkDeleting}
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              onClick={openBulkDeleteDialog}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {checkedVisibleCount === 0
                ? 'Eliminar'
                : `Eliminar (${checkedVisibleCount})`}
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1, display: 'block' }}>
        {hierarchyMode === 'collection'
          ? unusedOnly
            ? 'Marca varios para eliminarlos juntos. El click en el nombre abre el gráfico.'
            : 'Originales agrupados por tema.'
          : 'Por dashboard: borde sólido = original propio; punteado = referencia (solo lectura, abre el original).'}
      </Typography>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : hierarchyMode === 'collection' ? (
          filteredCollectionGroups.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5 }}>
              {isSearching
                ? `Sin resultados para «${searchQuery.trim()}».`
                : unusedOnly
                  ? 'No hay gráficos sin uso en la colección.'
                  : 'Aún no hay gráficos en la colección. Publica uno para guardarlo aquí.'}
            </Typography>
          ) : (
            filteredCollectionGroups.map(([groupName, items]) => (
              <Box key={groupName} sx={{ mb: 1 }}>
                <GroupHeader
                  title={groupName}
                  count={items.length}
                  expanded={expandedGroups[groupName] !== false}
                  onToggle={() =>
                    setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }))
                  }
                />
                <Collapse in={expandedGroups[groupName] !== false}>
                  <List disablePadding dense>
                    {items.map((item) => (
                      <OriginalListItem
                        key={item.id}
                        item={item}
                        selected={activeExampleId === item.id}
                        onSelect={handleSelectOriginal}
                        onDelete={handleDelete}
                        onAddToDashboard={handleAddToDashboard}
                        onCollectionMenu={(e, it) => {
                          setCollectionMenuAnchor(e.currentTarget);
                          setCollectionMenuItem(it);
                        }}
                        deleting={deletingId === item.id || bulkDeleting}
                        adding={addingId === item.id}
                        canAddToDashboard={Boolean(currentDashboardId && onAddOriginalToDashboard)}
                        selectable={unusedOnly}
                        checked={checkedIds.has(item.id)}
                        onToggleCheck={toggleChecked}
                      />
                    ))}
                  </List>
                </Collapse>
              </Box>
            ))
          )
        ) : filteredDashboardTree.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5 }}>
            {isSearching ? `Sin resultados para «${searchQuery.trim()}».` : 'No hay dashboards guardados.'}
          </Typography>
        ) : (
          filteredDashboardTree.map(({ dash, items }) => {
            const dashKey = String(dash.id);
            const isCurrent = String(dash.id) === String(currentDashboardId);
            const displayName =
              isCurrent && currentDashboardName?.trim()
                ? currentDashboardName.trim()
                : dash.name || `Dashboard ${dash.id}`;
            return (
              <Box key={dashKey} sx={{ mb: 1 }}>
                <GroupHeader
                  title={displayName}
                  count={items.length}
                  expanded={expandedDashboards[dashKey] !== false}
                  onToggle={() =>
                    setExpandedDashboards((prev) => ({ ...prev, [dashKey]: !prev[dashKey] }))
                  }
                  icon={DashboardOutlinedIcon}
                />
                {isCurrent && (
                  <Typography variant="caption" color="primary.main" sx={{ pl: 1, display: 'block', mb: 0.5 }}>
                    Dashboard abierto
                  </Typography>
                )}
                <Collapse in={expandedDashboards[dashKey] !== false}>
                  {items.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 2, display: 'block' }}>
                      Sin gráficos
                    </Typography>
                  ) : (
                    <List disablePadding dense>
                      {items.map(({ entry, original, isReference }) => (
                        <DashboardInstanceItem
                          key={`${dashKey}-${entry.id}`}
                          entry={entry}
                          original={original}
                          dashboardId={dash.id}
                          dashboardName={displayName}
                          isReference={isReference}
                          selected={
                            (isReference && activeReferenceInstanceId === entry.id) ||
                            (!isReference &&
                              (activeExampleId === entry.originalChartId ||
                                activeExampleId === entry.id))
                          }
                          onViewChartItem={onViewChartItem}
                          onMakeIndependent={isCurrent ? handleMakeIndependent : undefined}
                          onRestoreToGrid={isCurrent ? onRestoreToGrid : undefined}
                          makingIndependent={forkingId === entry.id}
                          isCurrentDashboard={isCurrent}
                        />
                      ))}
                    </List>
                  )}
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>

      <Divider />
      <Box sx={{ px: 1, py: 0.75 }}>
        <Button
          size="small"
          fullWidth
          onClick={() => setShowExamples((v) => !v)}
          sx={{ textTransform: 'none', fontSize: '0.75rem', justifyContent: 'space-between' }}
        >
          Ejemplos del producto ({builtins.length})
          {showExamples ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Button>
        <Collapse in={showExamples}>
          <List disablePadding dense sx={{ maxHeight: 160, overflow: 'auto', mt: 0.5 }}>
            {builtins.map((ex) => (
              <ExampleListItem
                key={ex.id}
                ex={ex}
                selected={activeExampleId === ex.id}
                onSelect={disabled ? undefined : onSelect}
              />
            ))}
          </List>
        </Collapse>
      </Box>

      <Menu
        anchorEl={collectionMenuAnchor}
        open={Boolean(collectionMenuAnchor)}
        onClose={() => {
          setCollectionMenuAnchor(null);
        }}
      >
        <MenuItem disabled sx={{ opacity: 1, fontSize: '0.75rem' }}>
          Mover a colección…
        </MenuItem>
        {collectionGroupNames.map((name) => (
          <MenuItem
            key={name}
            selected={collectionMenuItem?.collectionGroup === name}
            onClick={() => handleCollectionGroupPick(name)}
          >
            {name}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setCollectionMenuAnchor(null);
            setNewCollectionName('');
            setNewCollectionOpen(true);
          }}
        >
          + Nueva colección…
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(deleteQueue?.length)}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 80 }}
      >
        <DialogTitle>
          {deleteQueue?.length === 1 ? 'Eliminar gráfico' : 'Eliminar gráficos'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {deleteQueue?.length === 1
              ? `¿Eliminar «${deleteQueue[0]?.title || 'este gráfico'}» de la colección?`
              : `¿Eliminar los ${deleteQueue?.length || 0} gráficos seleccionados de la colección?`}
          </DialogContentText>
          {deleteQueue?.length > 1 && (
            <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
              {deleteQueue.slice(0, 20).map((item) => (
                <Typography key={item.id} variant="body2" component="li" sx={{ py: 0.25 }}>
                  {item.title || item.id}
                </Typography>
              ))}
              {deleteQueue.length > 20 && (
                <Typography variant="caption" color="text.secondary" component="li" sx={{ pt: 0.5 }}>
                  y {deleteQueue.length - 20} más
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={bulkDeleting}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteQueue}
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {bulkDeleting
              ? `Eliminando ${deleteQueue?.length || ''}…`
              : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newCollectionOpen}
        onClose={() => {
          if (bulkDeleting) return;
          setNewCollectionOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 80 }}
      >
        <DialogTitle>Nueva colección</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nombre"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCollectionName.trim()) {
                const name = newCollectionName.trim();
                setNewCollectionOpen(false);
                handleCollectionGroupPick(name);
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewCollectionOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!newCollectionName.trim()}
            onClick={() => {
              const name = newCollectionName.trim();
              setNewCollectionOpen(false);
              handleCollectionGroupPick(name);
            }}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
